import React, { useEffect, useState } from 'react';
import { Send, Stethoscope } from 'lucide-react';
import Modal from './Modal';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';
import { REQUEST_TYPES, URGENCY } from '../lib/clinicalMessages';

/**
 * "Send to clinician" — opens-or-creates a clinical request thread linked to a patient / request
 * entity. Recipients are always drawn from existing in-app users (server-derived; never free-text).
 */
export default function SendToClinician({ user, notify, navigate, patientId, requestType, relatedEntityId, label = 'Send to clinician' }) {
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [form, setForm] = useState({ recipient_id: '', request_type: requestType || 'general', urgency: 'ROUTINE', subject: '', body: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || DEMO) return;
    api('/clinical-message-recipients')
      .then(d => setRecipients(d.users || []))
      .catch(e => notify(e.message));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasCap(user, 'clinical_messages.create')) return null;

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (!form.recipient_id) throw new Error('Choose an authorized clinician.');
      if (!form.body.trim()) throw new Error('Write the request first.');
      const payload = {
        recipient_id: form.recipient_id,
        request_type: form.request_type,
        urgency: form.urgency,
        subject: form.subject.trim(),
        body: form.body.trim(),
        patient_id: patientId || undefined,
        related_entity_id: relatedEntityId || undefined,
      };
      const res = await api('/clinical-message-threads', { method: 'POST', body: payload });
      notify('Request sent to the clinician.');
      setOpen(false);
      setForm({ recipient_id: '', request_type: requestType || 'general', urgency: 'ROUTINE', subject: '', body: '' });
      if (navigate) navigate(`/messages/${res.thread_id}`);
    } catch (err) { notify(err.message); }
    finally { setBusy(false); }
  }

  return <>
    <button type="button" className="button ghost" onClick={() => setOpen(true)}><Stethoscope size={15}/> <span>{label}</span></button>
    {open && (
      <Modal title="Send to clinician" onClose={() => setOpen(false)} wide>
        <form className="admin-form" onSubmit={submit}>
          <div className="form-grid">
            <label>Authorized clinician
              <select required value={form.recipient_id} onChange={set('recipient_id')}>
                <option value="">Choose…</option>
                {recipients.map(u => <option key={u.id} value={u.id}>{u.name}{u.title ? ` (${u.title})` : ''}</option>)}
              </select>
            </label>
            <label>Request type
              <select value={form.request_type} onChange={set('request_type')}>
                {Object.entries(REQUEST_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label>Urgency
              <select value={form.urgency} onChange={set('urgency')}>
                {Object.entries(URGENCY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </label>
            <label>Subject<input maxLength={200} value={form.subject} onChange={set('subject')} placeholder="Brief subject (optional)"/></label>
            <label>Message<textarea rows={4} maxLength={8000} required value={form.body} onChange={set('body')} placeholder="Clinical question, summary, or specific action requested…"/></label>
          </div>
          <p className="cm-disclaimer" style={{ marginTop: 14 }}>Clinical messaging is for internal coordination only. It is not an emergency response system — use emergency protocols for urgent clinical events.</p>
          <button className="button primary" disabled={busy || !recipients.length}><Send size={14}/> <span>{busy ? 'Sending…' : 'Send request'}</span></button>
        </form>
      </Modal>
    )}
  </>;
}
